import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import type { Cuffs } from "../../../../types/Cuffs";
import CuffModal from './CuffModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

const CuffsPage: React.FC = () => {
  const [cuffs, setCuffs] = useState<Cuffs[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Cuffs | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchCuffs = async () => {
    try {
      const data = await apiRequest<Cuffs[]>({
        method: 'get',
        url: 'cuffs',
      });
      setCuffs(data);
    } catch (error) {
      showError('Failed to fetch cuffs');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCuffs();
  }, []);

  const handleEditClick = (record: Cuffs) => {
    setIsEditMode(true);
    setInitialValues({
      cuff_id: record.cuff_id,
      cuff_name: record.cuff_name,
      cuff_mesh_name: record.cuff_mesh_name,
      cuff_status: record.cuff_status,
      cuff_image: record.cuff_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchCuffs();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (cuff_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-cuff/${cuff_id}`,
      });
      showSuccess('Cuff deleted');
      fetchCuffs();
    } catch {
      showError('Failed to delete cuff');
    }
  };

  const handleStatusToggle = async (cuffId: number) => {
    setCuffs((prev) =>
      prev.map((item) =>
        item.cuff_id === cuffId
          ? { ...item, cuff_status: item.cuff_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-cuff/${cuffId}`,
      });
      showSuccess('Cuff status updated');
    } catch (error) {
      showError('Failed to update status');
      setCuffs((prev) =>
        prev.map((item) =>
          item.cuff_id === cuffId
            ? { ...item, cuff_status: item.cuff_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Cuffs>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<Cuffs> = [
    {
      title: 'Cuff',
      dataIndex: 'cuff_name',
      key: 'cuff_name',
      width: 500,
      filters: Array.from(new Set(cuffs.map((s) => s.cuff_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.cuff_name || null,
      onFilter: (value, record) => record.cuff_name.includes(value as string),
      sorter: (a, b) => a.cuff_name.localeCompare(b.cuff_name),
      sortOrder: sortedInfo.columnKey === 'cuff_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.cuff_name}</div>,
    },
    {
      title: 'Cuff Mesh Name',
      dataIndex: 'cuff_mesh_name',
      key: 'cuff_mesh_name',
      width: 500,
      filters: Array.from(new Set(cuffs.map((s) => s.cuff_mesh_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.cuff_mesh_name || null,
      onFilter: (value, record) => record.cuff_mesh_name.includes(value as string),
      sorter: (a, b) => a.cuff_mesh_name.localeCompare(b.cuff_mesh_name),
      sortOrder: sortedInfo.columnKey === 'cuff_mesh_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.cuff_mesh_name}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'cuff_image',
      key: 'cuff_image',
      width: 500,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.cuff_image}
            alt="cuff"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Cuffs) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.cuff_status}
                onChange={() => handleStatusToggle(record.cuff_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this cuff?"
              onConfirm={() => handleDelete(record.cuff_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Cuff</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Cuff
            </Button>
          </Flex>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Cuffs>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="cuff_id"
          dataSource={cuffs.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No cuff found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–{' '}
                {Math.min(pagination.current * pagination.pageSize, cuffs.length)} of{' '}
                {cuffs.length}
              </span>
              <Pagination
                {...pagination}
                total={cuffs.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <CuffModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        cuffs={cuffs}
      />
    </>
  );
};

export default CuffsPage;
